import { ChatBubbleOutline } from '@mui/icons-material'
import { Box, CircularProgress, Stack, Typography, styled } from '@mui/material'
import { FC, useEffect, useState } from 'react'

import MarkdownMessage from '../components/common/MarkdownMessage'
import chatConversationService, { ChatMessage } from '../services/chatConversation'

interface SharedChatPageProps {
  /** When true, use the email-gated private endpoint (requires an auth session). */
  isPrivate?: boolean
}

/**
 * Read-only viewer for a shared conversation. Rendered outside the authenticated
 * app shell (see AuthGate) so a public link works without signing in. The share
 * token is taken from the URL path:
 *   public:  /chat/share/:token
 *   private: /chat/private/share/:token
 */
const SharedChatPage: FC<SharedChatPageProps> = ({ isPrivate = false }) => {
  const [loading, setLoading] = useState(true)
  const [title, setTitle] = useState('Shared chat')
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const segments = window.location.pathname.split('/').filter(Boolean)
    const token = segments[segments.length - 1]
    if (!token) {
      setError('This share link is invalid.')
      setLoading(false)
      return
    }

    const request = isPrivate
      ? chatConversationService.getPrivateSharedMessages(token, { limit: 200 })
      : chatConversationService.getSharedMessages(token, { limit: 200 })

    request
      .then((res) => {
        setTitle(res?.title || 'Shared chat')
        setMessages(res?.data ?? [])
      })
      .catch((e) => {
        const status = (e as { status?: number })?.status
        if (status === 403) {
          setError('This chat was shared with a different account. Sign in with the invited email to view it.')
        } else {
          setError('This shared chat could not be found. The link may be invalid or no longer available.')
        }
      })
      .finally(() => setLoading(false))
  }, [isPrivate])

  return (
    <Root>
      <Header>
        <ChatBubbleOutline sx={{ color: '#2563eb' }} />
        <Typography fontWeight={700} noWrap>
          {title}
        </Typography>
        <Typography variant="caption" sx={{ color: '#6b7280', ml: 1 }}>
          Shared · read-only
        </Typography>
      </Header>

      <Scroll>
        {loading ? (
          <Stack alignItems="center" py={6}>
            <CircularProgress size={24} />
          </Stack>
        ) : error ? (
          <Typography sx={{ color: '#6b7280' }} textAlign="center" mt={8}>
            {error}
          </Typography>
        ) : messages.length === 0 ? (
          <Typography sx={{ color: '#6b7280' }} textAlign="center" mt={8}>
            This conversation has no messages yet.
          </Typography>
        ) : (
          <MsgCol>
            {messages.map((m) => (
              <Bubble key={m.id} role={m.role}>
                {m.role === 'assistant' ? <MarkdownMessage content={m.content} /> : m.content}
              </Bubble>
            ))}
          </MsgCol>
        )}
      </Scroll>
    </Root>
  )
}

const Root = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  height: '100vh',
  backgroundColor: '#fff',
})

const Header = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(1.5, 2),
  borderBottom: '1px solid #e5e7eb',
}))

const Scroll = styled(Box)(({ theme }) => ({
  flex: 1,
  overflow: 'auto',
  padding: theme.spacing(3, 2),
}))

const MsgCol = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 20,
  maxWidth: 860,
  margin: '0 auto',
  width: '100%',
})

const Bubble = styled(Box, { shouldForwardProp: (p) => p !== 'role' })<{
  role: 'user' | 'assistant'
}>(({ role }) => ({
  alignSelf: role === 'user' ? 'flex-end' : 'flex-start',
  maxWidth: '80%',
  padding: role === 'user' ? '10px 16px' : '12px 16px',
  borderRadius: role === 'user' ? 18 : 14,
  whiteSpace: 'pre-wrap',
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
  fontSize: 15,
  lineHeight: 1.5,
  backgroundColor: role === 'user' ? '#2f6df6' : '#f3f4f6',
  color: role === 'user' ? '#fff' : '#111827',
}))

export default SharedChatPage
